
import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { BookOpen, CheckCircle, ImageDown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import Callout from '@/components/ui/callout';

const LicensePage: React.FC = () => {
  return (
    <PageLayout 
      title="Creative Commons License" 
      icon={<BookOpen />}
      breadcrumbIcon={<ImageDown size={14} />}
      breadcrumbRootPath="/image-generator"
      breadcrumbs={[
        { label: 'Image Generator', path: '/image-generator' },
        { label: 'License', path: '/image-generator/license' }
      ]}
    >
      <Callout 
        variant="success" 
        icon={<CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />}
        title="All Public Images Are Free To Use!"
      >
        You can use, share, and modify any public image in our library for any purpose, even commercially.
      </Callout>
      
      <Card className="p-6 space-y-6 mt-4">
        <div className="space-y-3">
          <h2 className="text-2xl font-bold">Public Images License Information</h2>
          <p className="text-muted-foreground">
            All public images available in our Image Generator are provided under the Creative Commons Attribution 4.0 International License (CC BY 4.0).
          </p>
        </div>

        <Callout 
          variant="info" 
          title="What is Creative Commons?"
        >
          Creative Commons is a simple way to share creative work. Our CC BY 4.0 license means you can use any public image for free, as long as you give credit to the creator.
        </Callout>

        <div className="space-y-3">
          <h3 className="text-xl font-bold">What this means:</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>You are free to share and adapt the public images for any purpose, including commercial use.</li>
            <li>You must give appropriate credit, provide a link to the license, and indicate if changes were made.</li>
            <li>You may do so in any reasonable manner, but not in any way that suggests the licensor endorses you or your use.</li>
            <li>No additional restrictions — You may not apply legal terms or technological measures that legally restrict others from doing anything the license permits.</li>
          </ul>
        </div>

        <Callout 
          variant="warning" 
          title="You Must Give Credit"
        >
          <p>The only requirement is to give appropriate credit to the creator. Here's how:</p>
          <div className="bg-white dark:bg-slate-800 p-3 rounded-md mt-2 mb-1">
            <code>"Image by [Creator Name] via Image Generator, CC BY 4.0"</code>
          </div>
          <p className="mt-2">Replace [Creator Name] with the name of the image creator if available, or "DealDriver" if no specific creator is listed.</p>
        </Callout>
        
        <div className="space-y-1">
          <p className="text-muted-foreground">For the complete license terms, please visit:</p>
          <a 
            href="https://creativecommons.org/licenses/by/4.0/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700 transition-colors"
          >
            Creative Commons Attribution 4.0 International License
          </a>
        </div>
      </Card>
    </PageLayout>
  );
};

export default LicensePage;
